import { ReactElement } from 'react';

import { FEATURE_ENUM } from '@partoohub/api-client';
import {
    Illustration,
    IllustrationCategoryEnum,
    IllustrationElement,
    IllustrationVariousEnum,
} from '@partoohub/iconography';

import { FontAwesomeIconsPartooUsed, Icon, IconElement, IconPrefix } from '@partoohub/ui';

import { PARTOOROBOT_404_NOT_FOUND } from 'app/constants/robot';

import {
    ErrorPlaceholderEnum,
    NoDataPlaceholderEnum,
    PlaceholderCategory,
    PlaceholderType,
    SuccessPlaceholderEnum,
} from './DynamicPlaceholder.types';

export const getIllustrationComponent = (
    illustration: IllustrationElement,
    size: number,
): ReactElement<IllustrationElement> => (
    <Illustration
        dataTrackId={`placeholder__illustration__${illustration.type}-${illustration.name}`}
        width={`${size}px`}
        height={`${size}px`}
        illustration={illustration}
    />
);
export const getIconComponent = (icon: IconElement, size: number): ReactElement<IconElement> => (
    <Icon dataTrackId="placeholder__icon" iconSize={`${size}px`} icon={icon} color="secondary" />
);

/*
 * Default placeholders
 */
const illustrations = {
    [PlaceholderCategory.PageNotFound]: {
        type: IllustrationCategoryEnum.Various,
        name: IllustrationVariousEnum.Status404,
    },
    [PlaceholderCategory.NoPermissions]: {
        type: IllustrationCategoryEnum.Various,
        name: IllustrationVariousEnum.StatusWarning,
    },
    [PlaceholderCategory.NoBusiness]: {
        type: IllustrationCategoryEnum.Various,
        name: IllustrationVariousEnum.BusinessAddBusiness,
    },
    [PlaceholderCategory.NoResult]: {
        type: IllustrationCategoryEnum.Various,
        name: IllustrationVariousEnum.StatusNoResult,
    },
    [PlaceholderCategory.NoChartData]: [FontAwesomeIconsPartooUsed.faChartSimple, IconPrefix.SOLID],
};
const translations = {
    [PlaceholderCategory.PageNotFound]: {
        title: 'placeholder_page_not_found_title',
        description: 'placeholder_page_not_found_description',
    },
    [PlaceholderCategory.NoPermissions]: {
        title: 'placeholder_no_permissions_title',
        description: 'placeholder_no_permissions_description',
    },
    [PlaceholderCategory.NoBusiness]: {
        title: 'placeholder_no_business_title',
        description: 'placeholder_no_business_description',
    },
    [PlaceholderCategory.NoResult]: {
        title: 'placeholder_no_result_title',
        description: 'placeholder_no_result_description',
    },
    [PlaceholderCategory.NoChartData]: { description: 'placeholder_no_chart_data_description' },
};

/*
 * Dynamic placeholders
 */

// Not subscribed
const notSubscribedIllustrations: Partial<Record<FEATURE_ENUM, IllustrationVariousEnum>> = {
    [FEATURE_ENUM.POSTS]: IllustrationVariousEnum.FeaturesPosts,
    [FEATURE_ENUM.DIFFUSION]: IllustrationVariousEnum.StatusWarning,
    [FEATURE_ENUM.REVIEW_MANAGEMENT]: IllustrationVariousEnum.ReviewsNpsReview,
    [FEATURE_ENUM.REVIEW_INVITE]: IllustrationVariousEnum.FeaturesInvite,
    [FEATURE_ENUM.MESSAGES]: IllustrationVariousEnum.Messages,
    [FEATURE_ENUM.PRESENCE_ANALYTICS]: IllustrationVariousEnum.FeaturesRanking,
};
const notSubscribedTranslations: Partial<
    Record<FEATURE_ENUM, { title: string; description: string }>
> = {
    [FEATURE_ENUM.POSTS]: {
        title: 'placeholder_not_subscribed_posts_title',
        description: 'placeholder_not_subscribed_posts_description',
    },
    [FEATURE_ENUM.DIFFUSION]: {
        title: 'placeholder_not_subscribed_diffusion_title',
        description: 'placeholder_not_subscribed_diffusion_description',
    },
    [FEATURE_ENUM.REVIEW_MANAGEMENT]: {
        title: 'placeholder_not_subscribed_reviews_title',
        description: 'placeholder_not_subscribed_reviews_description',
    },
    [FEATURE_ENUM.REVIEW_INVITE]: {
        title: 'placeholder_not_subscribed_review_booster_title',
        description: 'placeholder_not_subscribed_review_booster_description',
    },
    [FEATURE_ENUM.MESSAGES]: {
        title: 'placeholder_not_subscribed_messages_title',
        description: 'placeholder_not_subscribed_messages_description',
    },
    [FEATURE_ENUM.PRESENCE_ANALYTICS]: {
        title: 'placeholder_not_subscribed_analytics_title',
        description: 'placeholder_not_subscribed_analytics_description',
    },
};

// No Data
const noDataIllustrations = {
    [NoDataPlaceholderEnum.MessagesClosedAssignedToMe]: IllustrationVariousEnum.StatusEmpty,
    [NoDataPlaceholderEnum.MessagesOpen]: IllustrationVariousEnum.StatusEmpty,
    [NoDataPlaceholderEnum.MessagesOpenUnassigned]: IllustrationVariousEnum.StatusEmpty,
    [NoDataPlaceholderEnum.ConnectionPlatformsApple]:
        IllustrationVariousEnum.ConnectionAppleAccount,
    [NoDataPlaceholderEnum.ReviewsTagsEmpty]: IllustrationVariousEnum.ReviewsTags,
};
const noDataTranslations = {
    [NoDataPlaceholderEnum.MessagesClosedAssignedToMe]: {
        title: 'placeholder_no_data_messages_closed_title',
        description: 'placeholder_no_data_messages_closed_description',
    },
    [NoDataPlaceholderEnum.MessagesOpen]: {
        title: 'placeholder_no_data_messages_open_title',
        description: 'placeholder_no_data_messages_open_description',
    },
    [NoDataPlaceholderEnum.MessagesOpenUnassigned]: {
        title: 'placeholder_no_data_messages_open_unassigned_title',
        description: 'placeholder_no_data_messages_open_unassigned_description',
    },
    [NoDataPlaceholderEnum.ConnectionPlatformsApple]: {
        title: 'placeholder_no_data_connection_platform_apple_title',
        description: 'placeholder_no_data_connection_platform_apple_description',
    },
    [NoDataPlaceholderEnum.ReviewsTagsEmpty]: {
        title: 'placeholder_no_data_reviews_tag_empty_title',
        description: 'placeholder_no_data_reviews_tag_empty_description',
    },
};

// Success
const successIllustrations = {
    [SuccessPlaceholderEnum.MessagesUpToDate]: IllustrationVariousEnum.MessagesTreated,
    [SuccessPlaceholderEnum.PlatformsUpToDate]: IllustrationVariousEnum.GoogleListingVerified,
};
const successTranslations = {
    [SuccessPlaceholderEnum.MessagesUpToDate]: {
        title: 'placeholder_success_messages_up_to_date_title',
        description: 'placeholder_success_messages_up_to_date_description',
    },
    [SuccessPlaceholderEnum.PlatformsUpToDate]: {
        title: 'placeholder_success_reviews_up_to_date_title',
        description: 'placeholder_success_reviews_up_to_date_description',
    },
};

// Error
const errorIllustrations = {
    [ErrorPlaceholderEnum.PlatformsVerification]: IllustrationVariousEnum.GoogleListingError,
};
const errorTranslations = {
    [ErrorPlaceholderEnum.PlatformsVerification]: {
        title: 'placeholder_error_platforms_verification_title',
        description: 'placeholder_error_platforms_verification_description',
    },
};

export type PlaceholderConfig = {
    title?: string;
    description?: string;
    illustration?: IllustrationElement;
    icon?: IconElement;
    size: string;
    partoorobot?: string;
};

export const getPlaceholderConfig = (
    category: PlaceholderCategory,
    type?: PlaceholderType,
): PlaceholderConfig => {
    let translation = {};
    let image = {};
    let size = 'medium';

    switch (category) {
        case PlaceholderCategory.PageNotFound:
        case PlaceholderCategory.NoPermissions:
        case PlaceholderCategory.NoBusiness:
        case PlaceholderCategory.NoResult:
            translation = translations[category];
            image = { illustration: illustrations[category] };
            break;
        case PlaceholderCategory.NoChartData:
            translation = translations[category];
            image = { icon: illustrations[category] };
            size = 'small';
            break;
        case PlaceholderCategory.NotSubscribed:
            translation = notSubscribedTranslations[type as FEATURE_ENUM] ?? {
                title: '',
                description: '',
            };
            image = {
                illustration: {
                    type: IllustrationCategoryEnum.Various,
                    name: notSubscribedIllustrations[type as FEATURE_ENUM],
                },
            };
            break;
        case PlaceholderCategory.NoData:
            translation = noDataTranslations[type as NoDataPlaceholderEnum];
            image = {
                illustration: {
                    type: IllustrationCategoryEnum.Various,
                    name: noDataIllustrations[type as NoDataPlaceholderEnum],
                },
            };
            break;
        case PlaceholderCategory.Success:
            translation = successTranslations[type as SuccessPlaceholderEnum];
            image = {
                illustration: {
                    type: IllustrationCategoryEnum.Various,
                    name: successIllustrations[type as SuccessPlaceholderEnum],
                },
            };
            break;
        case PlaceholderCategory.Error:
            translation = errorTranslations[type as ErrorPlaceholderEnum];
            image = {
                illustration: {
                    type: IllustrationCategoryEnum.Various,
                    name: errorIllustrations[type as ErrorPlaceholderEnum],
                },
            };
            break;
    }

    return {
        ...translation,
        ...image,
        size,
        partoorobot:
            category === PlaceholderCategory.PageNotFound ? PARTOOROBOT_404_NOT_FOUND : undefined,
    };
};
